/** Shared RBAC helpers — multi-role via `_effective_perms` / `role_ids`. */

/** ESS modules on the locked Employee role — never switch HR dashboard alone. */
const SELF_SERVICE_MODULES = new Set(["self_attendance", "holidays"]);

/**
 * Core HR ops modules — any grant switches to staff Dashboard / org-wide admin APIs.
 * Holidays are ESS (view) by default — managing holidays still needs Admin / custom role add.
 */
const STAFF_OPS_MODULES = new Set([
  "employees",
  "candidates",
  "attendance",
  "leaves",
  "approvals",
  "payroll",
  "statutory",
  "letters",
  "finance_po",
  "finance_invoices",
  "salary_revisions",
  "performance",
  "assets",
  "departments",
  "designations",
  "locations",
  "employee_ids",
  "companies",
  "letter_templates",
  "document_approvals",
  "id_cards",
  "users",
  "roles",
  "reports",
]);

export function isStrictAdmin(user) {
  if (!user) return false;
  return user.role === "admin" || (user.role_ids || []).includes("role_admin");
}

export function hasAction(user, module, action) {
  if (isStrictAdmin(user)) return true;
  const eff = user?._effective_perms;
  if (eff && eff[module] && eff[module][action]) return true;
  if (user?.role === "hr_executive") {
    const flag = (user?.permissions || {})[module];
    if (!flag) return false;
    return action !== "delete" && action !== "approve";
  }
  return false;
}

export function canSeeModule(
  user,
  module,
  actions = ["view", "add", "edit", "delete", "approve", "export"],
) {
  if (isStrictAdmin(user)) return true;
  return actions.some((a) => hasAction(user, module, a));
}

/** HR / admin dashboard (stats, org birthdays, etc.). */
export function hasStaffAccess(user) {
  if (!user) return false;
  if (user.role === "admin" || user.role === "hr_executive") return true;
  if ((user.role_ids || []).includes("role_admin")) return true;
  const eff = user._effective_perms || {};
  for (const mod of STAFF_OPS_MODULES) {
    const actions = eff[mod];
    if (actions && Object.values(actions).some(Boolean)) return true;
  }
  return false;
}

/**
 * May open staff module routes (AdminRoute) — custom role grants (Payroll, Employees, …).
 * Pure ESS (attendance / docs / holidays view) stays on self-service dashboard.
 */
export function hasElevatedModuleAccess(user) {
  if (hasStaffAccess(user)) return true;
  const eff = user?._effective_perms || {};
  for (const [mod, actions] of Object.entries(eff)) {
    if (SELF_SERVICE_MODULES.has(mod)) continue;
    if (actions && Object.values(actions).some(Boolean)) return true;
  }
  return false;
}

export function showSelfServiceNav(user) {
  return Boolean(user?.employee_id) || user?.role === "employee";
}
